import { type Table } from '@tanstack/react-table';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import { Text } from '../Typography';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';
import { cn } from '../utils/cn';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions?: number[];
  isPending?: boolean;
}

export function DataTablePagination<TData>({
  table,
  isPending = false,
  pageSizeOptions = [10, 20, 30, 40, 50],
}: DataTablePaginationProps<TData>) {
  // An empty result set has no pages, and the table can still hold a page index
  // from before it emptied — clamp both so neither "of 0" nor "Page 2 of 1".
  const tablePageCount = table.getPageCount();
  const pageCount = Math.max(1, tablePageCount);
  const pageIndex = Math.min(
    table.getState().pagination.pageIndex,
    pageCount - 1,
  );
  const currentPage = pageIndex + 1;
  // Navigate off the clamped index so the controls agree with the label, but
  // keep the way back enabled while a stale index sits past real rows.
  const canGoBack = tablePageCount > 0 && table.getCanPreviousPage();
  const canGoForward = pageIndex < pageCount - 1;

  return (
    <div
      className={cn(
        'flex w-full flex-col-reverse items-center justify-end gap-4 overflow-auto p-1 sm:flex-row sm:gap-8',
        'border-t border-border-primary transition-opacity duration-200 ease-in-out',
        { 'pointer-events-none opacity-50': isPending },
      )}
    >
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
        <Stack
          className="space-x-2"
          direction="horizontal"
          align="center"
          fullWidth={false}
        >
          <Text className="whitespace-nowrap">Rows per page</Text>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-18">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
        <Stack
          className="text-sm font-medium"
          direction="horizontal"
          align="center"
          justify="center"
          fullWidth={false}
        >
          Page {currentPage} of {pageCount}
        </Stack>
        <Stack
          className="space-x-2"
          direction="horizontal"
          align="center"
          fullWidth={false}
        >
          <Button
            aria-label="Go to first page"
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!canGoBack}
          >
            <Icon name="ChevronsLeft" />
          </Button>
          <Button
            aria-label="Go to previous page"
            variant="outline"
            className="size-8"
            onClick={() => table.setPageIndex(pageIndex - 1)}
            disabled={!canGoBack}
          >
            <Icon name="ChevronLeft" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            className="size-8"
            onClick={() => table.setPageIndex(pageIndex + 1)}
            disabled={!canGoForward}
          >
            <Icon name="ChevronRight" />
          </Button>
          <Button
            aria-label="Go to last page"
            variant="outline"
            className="hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!canGoForward}
          >
            <Icon name="ChevronsRight" />
          </Button>
        </Stack>
      </div>
    </div>
  );
}
